import { useEffect, useRef, useState } from "react";
import { BookOpen, MapPin, Compass, Sparkles, ArrowRight } from "lucide-react";
import { Flag } from "./Flag";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { guides: [], destinations: [], sections: [], total: 0 };

/* Debounced public search over guides, destinations and site sections (GET /api/public/search). */
export function useSiteSearch(q, { scope = "all", audience = "", enabled = true } = {}) {
  const [res, setRes] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    const text = (q || "").trim();
    if (!enabled || text.length < 2) { setRes(EMPTY); setBusy(false); return undefined; }
    const my = ++seq.current;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/public/search?q=${encodeURIComponent(text)}&scope=${scope}&audience=${audience}`);
        const d = r.ok ? await r.json() : EMPTY;
        if (seq.current === my) setRes(d);
      } catch { if (seq.current === my) setRes(EMPTY); }
      finally { if (seq.current === my) setBusy(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, scope, audience, enabled]);
  return { ...res, busy, items: [...res.guides, ...res.destinations, ...res.sections] };
}

const ICON = { guide: BookOpen, destination: MapPin, section: Compass };
const GROUP = { guide: "Guides", destination: "Destinations", section: "On this site" };

/* Dropdown under a search box. `items` come from useSiteSearch; the last row always offers the AI answer. */
export function SearchDropdown({ q, items, busy, active, onPick, onAsk, askLabel = "Ask Vistolane", testId = "site-search" }) {
  const text = (q || "").trim();
  if (text.length < 2) return null;
  const rows = items.map((it, i) => ({ ...it, i }));
  const groups = ["guide", "destination", "section"].map((t) => [t, rows.filter((r) => r.type === t)]).filter(([, r]) => r.length);
  const askIndex = rows.length;
  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-card border border-separator bg-background text-left shadow-pop" role="listbox" data-testid={testId}>
      <div className="max-h-[min(60vh,420px)] overflow-y-auto py-1">
        {groups.map(([t, list]) => (
          <div key={t}>
            <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-label2">{GROUP[t]}</p>
            {list.map((r) => { const I = ICON[r.type]; return (
              <button key={`${r.type}-${r.slug || r.to}`} type="button" role="option" aria-selected={active === r.i} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(r)} className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors ${active === r.i ? "bg-gray6" : "hover:bg-gray6/70"}`} data-testid={`${testId}-item-${r.type}`}>
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent/60 text-tint-strong">{r.flag ? <Flag flag={r.flag} size={16} /> : <I className="h-3.5 w-3.5" />}</span>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-foreground">{r.title}</span>{r.sub && <span className="block truncate text-xs text-label2">{r.sub}</span>}</span>
                {r.type === "guide" && r.read && <span className="mt-1 shrink-0 text-[11px] text-label2">{r.read} min</span>}
              </button>
            ); })}
          </div>
        ))}
        {!rows.length && !busy && <p className="px-4 py-3 text-xs text-label2" data-testid={`${testId}-none`}>No guide or page matches “{text}” — ask the assistant instead.</p>}
        {busy && !rows.length && <p className="px-4 py-3 text-xs text-label2">Searching…</p>}
      </div>
      {onAsk && (
        <button type="button" role="option" aria-selected={active === askIndex} onMouseDown={(e) => e.preventDefault()} onClick={() => onAsk(text)} className={`flex w-full items-center gap-3 border-t border-separator px-4 py-3 text-left text-sm transition-colors ${active === askIndex ? "bg-accent/40" : "hover:bg-accent/30"}`} data-testid={`${testId}-ask`}>
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-tint text-white"><Sparkles className="h-3.5 w-3.5" /></span>
          <span className="min-w-0 flex-1"><span className="font-semibold">{askLabel}:</span> <span className="text-label2">“{text}”</span></span>
          <ArrowRight className="h-4 w-4 shrink-0 text-label2" />
        </button>
      )}
    </div>
  );
}

/* Keyboard handling shared by every search box: arrows move, Enter picks the active row (default = first match, or Ask when nothing matches), Escape closes. */
export function useSearchKeys({ items, onPick, onAsk, open, setOpen, preferAsk = false }) {
  const [active, setActive] = useState(-1);
  useEffect(() => { setActive(items.length && !(preferAsk && onAsk) ? 0 : onAsk ? items.length : -1); }, [items.length, onAsk, preferAsk]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = items.length + (onAsk ? 1 : 0);
  const onKeyDown = (e, text) => {
    if (!open) return false;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % Math.max(1, total)); return true; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + total) % Math.max(1, total)); return true; }
    if (e.key === "Escape") { setOpen(false); return true; }
    if (e.key === "Enter") {
      if (active >= 0 && active < items.length) { e.preventDefault(); onPick(items[active]); return true; }
      if (onAsk && (active === items.length || !items.length)) { e.preventDefault(); onAsk(text); return true; }
    }
    return false;
  };
  return { active, setActive, onKeyDown };
}
